import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { toast } from '../../components/toast.js';
import { ROSTER_EMPTY, rosterCandidates, rosterPath } from '../../core/parity.js';

function countLabel(n) {
  return String(n).padStart(2, '0');
}

function canRemove(me, members) {
  if (me && me.role === 'admin') return true;
  const mine = (members || []).find((member) => member.id === me.id);
  return !!(mine && mine.role === 'admin');
}

/**
 * @param {{ me: object, productId?: string, onChanged?: () => void }} opts
 */
export function mountMembers(opts) {
  const list = el('div', { class: 'members-list' });
  const addBox = el('div', { class: 'members-add' });
  const headCount = el('span', {}, 'Members');
  const panel = el('aside', { class: 'members', 'aria-label': 'Chat members' },
    el('div', { class: 'members-head' },
      headCount,
      el('button', {
        type: 'button',
        class: 'icon-btn',
        'aria-label': 'Hide members panel',
        onClick: () => { if (opts.onHide) opts.onHide(); },
      }, '×')),
    list,
    addBox);
  let groupId = '';
  let members = [];
  let online = new Set();
  let people = [];
  let rosterError = '';
  let rosterReady = false;

  function paint() {
    clear(list);
    headCount.textContent = `Members · ${countLabel(members.length)}`;
    for (const member of members) {
      const agent = member.is_agent || member.role === 'agent' || member.id === 'agent';
      const you = !agent && opts.me && member.id === opts.me.id;
      const on = agent || you || online.has(member.id);
      const row = el('div', { class: 'member-row' },
        el('span', { class: agent ? 'msg-mark' : 'member-initial', 'aria-hidden': 'true' }, agent ? '✦' : initial(member.name || member.email)),
        el('span', { class: 'member-id' },
          el('span', { class: 'member-name' }, agent ? 'Bonnie' : `${member.name || member.email || 'Teammate'}${you ? ' (you)' : ''}`),
          el('span', { class: 'member-role' }, agent ? 'Auto model · always present' : roleLine(member))),
        el('span', { class: on ? 'member-on' : 'member-off' }, on ? '● On' : '○ Away'));
      if (!agent && canRemove(opts.me, members)) {
        row.append(el('button', {
          type: 'button',
          class: 'tool-btn',
          'aria-label': `Remove ${member.name || 'member'}`,
          onClick: () => remove(member.id),
        }, 'Remove'));
      }
      list.append(row);
    }
  }

  function paintAdd() {
    clear(addBox);
    if (!canRemove(opts.me, members)) {
      addBox.append(el('p', { class: 'members-note' }, 'An admin can add or remove people.'));
      return;
    }
    const candidates = rosterCandidates(people, members);
    addBox.append(el('label', { class: 'members-label' }, 'Add someone'));
    if (!rosterReady) {
      addBox.append(el('p', { class: 'members-note' }, 'Loading people…'));
      return;
    }
    if (rosterError) {
      addBox.append(el('p', { class: 'members-note' }, rosterError));
      return;
    }
    if (!opts.productId) {
      addBox.append(el('p', { class: 'members-note' }, 'Select a product to add people.'));
      return;
    }
    if (!candidates.length) {
      addBox.append(el('p', { class: 'members-note' }, ROSTER_EMPTY));
      return;
    }
    const search = el('input', {
      type: 'search',
      class: 'member-search',
      placeholder: 'Search teammates…',
      'aria-label': 'Add someone',
    });
    const hits = el('div', { class: 'member-hits' });
    search.addEventListener('input', () => paintHits(search, hits, candidates));
    addBox.append(
      search,
      hits,
      el('p', { class: 'members-note' }, 'Only people with access to this product can be added.'));
  }

  function paintHits(search, hits, candidates) {
    clear(hits);
    const q = search.value.trim().toLowerCase();
    if (!q) return;
    const matches = candidates.filter((person) => {
      const blob = `${person.name || ''} ${person.email || ''}`.toLowerCase();
      return blob.includes(q);
    }).slice(0, 6);
    if (!matches.length) {
      hits.append(el('p', { class: 'members-note' }, 'No matching teammate.'));
      return;
    }
    for (const person of matches) {
      hits.append(el('button', {
        type: 'button',
        class: 'mention-hit',
        onClick: () => add(person.id, search, hits),
      }, person.name || person.email));
    }
  }

  async function add(userId, search, hits) {
    const res = await api(`/chat/${encodeURIComponent(groupId)}/members`, {
      method: 'POST',
      body: { memberIds: [userId] },
    });
    if (!res || res.error) {
      toast((res && res.error) || 'Could not add that person', 'error');
      return;
    }
    search.value = '';
    clear(hits);
    await load(groupId);
    if (opts.onChanged) opts.onChanged();
  }

  async function remove(userId) {
    const res = await api(`/chat/${encodeURIComponent(groupId)}/members/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    });
    if (!res || res.error) {
      toast((res && res.error) || 'Could not remove that person', 'error');
      return;
    }
    await load(groupId);
    if (opts.onChanged) opts.onChanged();
  }

  async function load(id) {
    groupId = id;
    const res = await api(`/chat/${encodeURIComponent(id)}/members`);
    if (!res || res.error) {
      clear(list);
      list.append(el('p', { class: 'members-note' }, (res && res.error) || 'Members could not be loaded.'));
      return;
    }
    members = res.members || [];
    paint();
    rosterError = '';
    people = [];
    rosterReady = false;
    if (canRemove(opts.me, members) && opts.productId) {
      paintAdd();
      const roster = await api(rosterPath(opts.productId));
      if (!roster || roster.error) rosterError = (roster && roster.error) || 'Could not load people';
      else people = roster.users || [];
    }
    rosterReady = true;
    paintAdd();
    return members;
  }

  paintAdd();
  return {
    node: panel,
    load,
    setOnline(next) {
      online = next;
      paint();
    },
  };
}

function initial(name) {
  const text = String(name || '?').trim();
  return text ? text.charAt(0).toUpperCase() : '?';
}

function roleLine(member) {
  const role = member.user_role || member.role || 'member';
  return role === 'admin' ? 'Admin' : 'Member';
}
